import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Edit2, Trash2, Clock, CheckCircle2, Circle, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TodoPanel = ({
  isOpen,
  onClose,
  selectedWeek,
  onTaskDragStart,
  onCreateEntry,
  initialTaskId = null,
  onInitialTaskHandled
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('This Week'); // Week label (e.g., 'This Week', 'Last Week', etc.)
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [pendingTaskId, setPendingTaskId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'normal',
    repeat: 'none'
  });

  // Calculate weeks for tabs
  const getWeekLabel = (offset) => {
    const date = new Date();
    date.setDate(date.getDate() + (offset * 7));
    const monday = new Date(date);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
    
    // Generate label based on offset
    let label;
    if (offset === 0) {
      label = 'This Week';
    } else if (offset === -1) {
      label = 'Last Week';
    } else if (offset < 0) {
      label = `${Math.abs(offset)} Weeks Ago`;
    } else {
      label = `Week ${offset + 1}`;
    }
    
    return {
      label: label,
      range: `${formatDate(monday)} - ${formatDate(sunday)}`,
      weekStart: monday.toISOString().split('T')[0]
    };
  };

  // Include 2 previous weeks, current week, and 4 future weeks
  const weeks = [-2, -1, 0, 1, 2, 3, 4].map(getWeekLabel);

  // Fetch tasks for the selected week
  const fetchTasks = async (weekStart) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tasks`, {
        params: { week: weekStart },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const currentWeek = weeks.find(w => w.label === activeTab) || weeks[0];
      fetchTasks(currentWeek.weekStart);
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (initialTaskId) {
      setPendingTaskId(initialTaskId);
    }
  }, [initialTaskId]);

  useEffect(() => {
    if (!isOpen || !pendingTaskId || loading) return;

    const taskToOpen = tasks.find((task) => task.id === pendingTaskId);
    if (taskToOpen) {
      openEditModal(taskToOpen);
      setPendingTaskId(null);
      if (onInitialTaskHandled) {
        onInitialTaskHandled(taskToOpen);
      }
    }
  }, [isOpen, pendingTaskId, loading, tasks, onInitialTaskHandled]);

  // Create or update task
  const handleSaveTask = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const currentWeek = weeks.find(w => w.label === activeTab) || weeks[0];
      
      const payload = {
        ...formData,
        week_start_date: currentWeek.weekStart,
        repeat: formData.repeat === 'none' ? null : formData.repeat
      };

      if (editingTask) {
        // Update existing task
        await axios.patch(
          `${API_URL}/api/tasks/${editingTask.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task updated!');
      } else {
        // Create new task
        await axios.post(
          `${API_URL}/api/tasks`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task created!');
      }

      setShowAddModal(false);
      setEditingTask(null);
      setFormData({ title: '', description: '', due_date: '', priority: 'normal', repeat: 'none' });
      fetchTasks(currentWeek.weekStart);
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save task');
    }
  };

  // Toggle task completion
  const toggleComplete = async (task) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/tasks/${task.id}`,
        { is_completed: !task.is_completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(tasks.map(t => 
        t.id === task.id ? { ...t, is_completed: !t.is_completed } : t
      ));
      toast.success(task.is_completed ? 'Task reopened' : 'Task completed! 🎉');
    } catch (error) {
      console.error('Error toggling task:', error);
      toast.error('Failed to update task');
    }
  };

  // Delete task
  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Open edit modal
  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority,
      repeat: task.repeat || 'none'
    });
    setShowAddModal(true);
  };

  // Group tasks by priority
  const groupedTasks = {
    high: tasks.filter(t => t.priority === 'high' && !t.is_completed),
    normal: tasks.filter(t => t.priority === 'normal' && !t.is_completed),
    low: tasks.filter(t => t.priority === 'low' && !t.is_completed),
    completed: tasks.filter(t => t.is_completed)
  };

  // Calculate progress
  const totalTasks = tasks.length;
  const completedCount = tasks.filter(t => t.is_completed).length;
  const progressPercent = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  const getPriorityColor = (priority) => {
    return {
      high: 'border-l-4 border-red-500 bg-red-50',
      normal: 'border-l-4 border-blue-500 bg-blue-50',
      low: 'border-l-4 border-gray-500 bg-gray-50'
    }[priority] || '';
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed right-0 top-0 bg-white shadow-2xl z-50 transition-all duration-300 ${
        collapsed ? 'w-12' : 'w-96'
      }`}
      style={{ 
        borderLeft: '2px solid #e5e7eb',
        height: '100vh',
        paddingBottom: '80px' // Space for bottom nav
      }}
    >
      {/* Collapse/Expand Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -left-6 top-20 bg-cyan-500 text-white p-2 rounded-l-lg shadow-lg hover:bg-cyan-600"
      >
        {collapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
      </button>

      {!collapsed && (
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-cyan-500 to-teal-500">
            <div className="flex items-center justify-between text-white">
              <div>
                <h2 className="text-xl font-bold">Weekly To-Do</h2>
                <p className="text-xs opacity-90">Plan your week ahead</p>
              </div>
              <button onClick={onClose} className="hover:bg-white/20 p-2 rounded">
                <X size={20} />
              </button>
            </div>

            {/* Progress Ring */}
            <div className="mt-4 flex items-center gap-3">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="rgba(255,255,255,0.3)"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="white"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - progressPercent / 100)}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">
                  {progressPercent}%
                </div>
              </div>
              <div className="text-white text-sm">
                <div className="font-semibold">{completedCount} of {totalTasks} done</div>
                <div className="text-xs opacity-90">
                  {totalTasks - completedCount} remaining
                </div>
              </div>
            </div>
          </div>

          {/* Week Tabs */}
          <div className="flex overflow-x-auto border-b bg-gray-50">
            {weeks.map((week, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(week.label)}
                className={`flex-1 min-w-fit px-3 py-3 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === week.label
                    ? 'border-cyan-500 text-cyan-600 bg-white'
                    : 'border-transparent text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div>{week.label}</div>
                <div className="text-[10px] text-gray-500">{week.range}</div>
              </button>
            ))}
          </div>

          {/* Tasks List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading tasks...</div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Circle size={48} className="mx-auto mb-2 opacity-30" />
                <p>No tasks yet</p>
                <p className="text-xs mt-1">Click "Add Task" to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* High Priority */}
                {groupedTasks.high.length > 0 && (
                  <TaskGroup
                    title="High Priority"
                    count={groupedTasks.high.length}
                    tasks={groupedTasks.high}
                    onToggle={toggleComplete}
                    onEdit={openEditModal}
                    onDelete={deleteTask}
                    onDragStart={onTaskDragStart}
                    onLogTime={onCreateEntry}
                  />
                )}

                {/* Normal Priority */}
                {groupedTasks.normal.length > 0 && (
                  <TaskGroup
                    title="Normal"
                    count={groupedTasks.normal.length}
                    tasks={groupedTasks.normal}
                    onToggle={toggleComplete}
                    onEdit={openEditModal}
                    onDelete={deleteTask}
                    onDragStart={onTaskDragStart}
                    onLogTime={onCreateEntry}
                  />
                )}

                {/* Low Priority */}
                {groupedTasks.low.length > 0 && (
                  <TaskGroup
                    title="Low Priority"
                    count={groupedTasks.low.length}
                    tasks={groupedTasks.low}
                    onToggle={toggleComplete}
                    onEdit={openEditModal}
                    onDelete={deleteTask}
                    onDragStart={onTaskDragStart}
                    onLogTime={onCreateEntry}
                  />
                )}

                {/* Completed */}
                {groupedTasks.completed.length > 0 && (
                  <TaskGroup
                    title="Completed"
                    count={groupedTasks.completed.length}
                    tasks={groupedTasks.completed}
                    onToggle={toggleComplete}
                    onEdit={openEditModal}
                    onDelete={deleteTask}
                    collapsed={true}
                  />
                )}
              </div>
            )}
          </div>

          {/* Add Task Button */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 transition-colors">
            <Button
              onClick={() => {
                setEditingTask(null);
                setFormData({ title: '', description: '', due_date: '', priority: 'normal', repeat: 'none' });
                setShowAddModal(true);
              }}
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold"
            >
              <Plus size={18} className="mr-2" />
              Add Task
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 transition-colors">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100 transition-colors">
                {editingTask ? 'Edit Task' : 'New Task'}
              </h3>

              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">Task Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="What needs to be done?"
                    className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">Description</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Add details..."
                    className="w-full mt-1 p-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 rounded-md resize-none h-20 transition-colors"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">Due Date (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 transition-colors"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">Priority</Label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full mt-1 p-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md transition-colors"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <Label className="text-gray-700 dark:text-gray-300 transition-colors">Repeat</Label>
                  <select
                    value={formData.repeat}
                    onChange={(e) => setFormData({ ...formData, repeat: e.target.value })}
                    className="w-full mt-1 p-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md transition-colors"
                  >
                    <option value="none">No repeat</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingTask(null);
                  }}
                  variant="outline"
                  className="flex-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveTask}
                  className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  {editingTask ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// TaskGroup Component
const TaskGroup = ({ title, count, tasks, onToggle, onEdit, onDelete, onDragStart, onLogTime, collapsed: initialCollapsed = false }) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  return (
    <div className="mb-4">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 mb-2 hover:text-cyan-600"
      >
        <span>{title} ({count})</span>
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
      </button>

      {!collapsed && (
        <div className="space-y-2">
          {tasks.map(task => (
            <TaskItem
              key={task.id}
              task={task}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onDragStart={onDragStart}
              onLogTime={onLogTime}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// TaskItem Component
const TaskItem = ({ task, onToggle, onEdit, onDelete, onDragStart, onLogTime }) => {
  const getPriorityClass = () => {
    return {
      high: 'border-l-4 border-red-500 bg-red-50',
      normal: 'border-l-4 border-blue-500 bg-blue-50',
      low: 'border-l-4 border-gray-500 bg-gray-50'
    }[task.priority] || '';
  };

  return (
    <div
      draggable={!task.is_completed}
      onDragStart={(e) => {
        if (onDragStart && !task.is_completed) {
          onDragStart(e, task);
        }
      }}
      className={`p-3 rounded-lg shadow-sm transition-all hover:shadow-md ${
        task.is_completed
          ? 'bg-gray-100 opacity-60'
          : getPriorityClass()
      } ${!task.is_completed ? 'cursor-move' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={() => onToggle(task)}
          className="mt-1 text-gray-600 hover:text-cyan-600"
        >
          {task.is_completed ? (
            <CheckCircle2 size={20} className="text-green-500" />
          ) : (
            <Circle size={20} />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className={`font-medium ${task.is_completed ? 'line-through text-gray-500' : 'text-gray-800'}`}>
            {task.title}
          </div>
          {task.description && (
            <div className="text-xs text-gray-600 mt-1">{task.description}</div>
          )}
          {task.due_date && (
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Clock size={12} />
              Due: {new Date(task.due_date).toLocaleDateString()}
            </div>
          )}
          {task.linked_entry_summary && (
            <div className="text-xs text-cyan-600 mt-1">
              🔗 {task.linked_entry_summary}
            </div>
          )}
        </div>

        {!task.is_completed && (
          <div className="flex items-center gap-1">
            {onLogTime && (
              <button
                onClick={() => onLogTime(task)}
                className="p-1 hover:bg-white rounded"
                title="Log time for this task"
              >
                <Clock size={16} className="text-cyan-600" />
              </button>
            )}
            <button
              onClick={() => onEdit(task)}
              className="p-1 hover:bg-white rounded"
            >
              <Edit2 size={16} className="text-blue-600" />
            </button>
            <button
              onClick={() => onDelete(task.id)}
              className="p-1 hover:bg-white rounded"
            >
              <Trash2 size={16} className="text-red-600" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoPanel;
